import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'

import type { AppRouter } from '@viapeople/api'
import { Input, Modal, SpinnerWithBackground, Text, Toggle } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { trpc } from '../../utils/trpc'
import { DataFeedColumnMappingFields } from './DataFeedColumnMappingFields'
import { DataFeedRecentRunsSection } from './DataFeedRecentRunsSection'
import {
	buildColumnMappingPayload,
	type DataFeedColumnMappingFormSlice,
	defaultColumnMappingFormValues,
	getColumnMappingFormValues,
} from './dataFeedColumnMapping'
import { SftpKeyStatusBanner } from './SftpKeyStatusBanner'
import { useDataFeedConfigMutations } from './useDataFeedConfigMutations'

type RouterOutput = inferRouterOutputs<AppRouter>
type DataFeedConfig = NonNullable<RouterOutput['admin']['integrations']['dataFeed']['getByOrganization']>

interface DataFeedConfigModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	existingConfig?: DataFeedConfig | null
}

const DEFAULT_FILE_EXTENSIONS: ('csv' | 'xlsx' | 'xls')[] = ['csv', 'xlsx', 'xls']

export function DataFeedConfigModal({ isOpen, onClose, organizationId, existingConfig }: DataFeedConfigModalProps) {
	const [formData, setFormData] = useState<
		{
			name: string
			enabled: boolean
			sftpUsername: string
			bucketName: string
			fileExtensions: ('csv' | 'xlsx' | 'xls')[]
		} & DataFeedColumnMappingFormSlice
	>({
		name: '',
		enabled: true,
		sftpUsername: '',
		bucketName: '',
		fileExtensions: DEFAULT_FILE_EXTENSIONS,
		...defaultColumnMappingFormValues,
	})

	useEffect(() => {
		if (existingConfig) {
			const sftpConfig = existingConfig.adapterConfig as {
				sftpUsername?: string
				bucketName?: string
				fileExtensions?: ('csv' | 'xlsx' | 'xls')[]
			} | null
			setFormData({
				name: existingConfig.name || '',
				enabled: existingConfig.enabled ?? true,
				sftpUsername: sftpConfig?.sftpUsername || '',
				bucketName: sftpConfig?.bucketName || '',
				fileExtensions: sftpConfig?.fileExtensions || DEFAULT_FILE_EXTENSIONS,
				...getColumnMappingFormValues(existingConfig.columnMapping),
			})
		} else {
			setFormData({
				name: '',
				enabled: true,
				sftpUsername: '',
				bucketName: '',
				fileExtensions: DEFAULT_FILE_EXTENSIONS,
				...defaultColumnMappingFormValues,
			})
		}
	}, [existingConfig])

	const { data: sftpKeyData, isLoading: isSftpKeysLoading } = useQuery({
		...trpc.admin.sftp.listKeys.queryOptions({ organizationId }),
		enabled: !!existingConfig,
	})

	const { runs, createMutation, updateMutation, deleteMutation } = useDataFeedConfigMutations({
		organizationId,
		adapterType: 'sftp',
		existingConfigId: existingConfig?.id,
		label: 'Data feed',
		onClose,
	})

	const submitForm = () => {
		const payload = {
			name: formData.name,
			enabled: formData.enabled,
			adapterConfig: {
				sftpUsername: formData.sftpUsername,
				bucketName: formData.bucketName,
				fileExtensions: formData.fileExtensions,
			},
			columnMapping: buildColumnMappingPayload(formData),
		}

		if (existingConfig) {
			updateMutation.mutate({ id: existingConfig.id, ...payload })
		} else {
			createMutation.mutate({ organizationId, adapterType: 'sftp', ...payload })
		}
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		submitForm()
	}

	const handleDelete = () => {
		if (
			existingConfig &&
			window.confirm('Delete this data feed configuration? This will also remove all run history and cannot be undone.')
		) {
			deleteMutation.mutate({ id: existingConfig.id })
		}
	}

	const toggleExtension = (ext: 'csv' | 'xlsx' | 'xls') => {
		setFormData((prev) => {
			const exts = prev.fileExtensions.includes(ext)
				? prev.fileExtensions.filter((e) => e !== ext)
				: [...prev.fileExtensions, ext]
			return { ...prev, fileExtensions: exts.length > 0 ? exts : prev.fileExtensions }
		})
	}

	const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
	const isFormValid =
		formData.name &&
		formData.sftpUsername &&
		formData.bucketName &&
		formData.fileExtensions.length > 0 &&
		formData.firstName &&
		formData.lastName &&
		formData.email &&
		formData.externalId

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title='Configure Data Feed (SFTP)'
			buttons={[
				{
					children: 'Cancel',
					onClick: onClose,
					variant: 'secondary',
					disabled: isLoading,
				},
				...(existingConfig
					? [
							{
								children: 'Delete',
								onClick: handleDelete,
								variant: 'destructive' as const,
								disabled: isLoading,
							},
						]
					: []),
				{
					children: existingConfig ? 'Update' : 'Save',
					onClick: submitForm,
					variant: 'primary',
					disabled: !isFormValid || isLoading,
				},
			]}>
			{existingConfig && (
				<SftpKeyStatusBanner
					organizationId={organizationId}
					keys={sftpKeyData?.keys ?? []}
					connectionInfo={sftpKeyData?.connectionInfo ?? null}
					isLoading={isSftpKeysLoading}
				/>
			)}

			<form onSubmit={handleSubmit} className='space-y-4'>
				<Text variant='small' className='mb-4 text-neutral-600'>
					Configure your SFTP data feed settings. Files uploaded to the SFTP share will be automatically processed on an
					hourly schedule.
				</Text>

				<Input
					label='Name'
					value={formData.name}
					onChange={(e) => setFormData({ ...formData, name: e.target.value })}
					placeholder='e.g., Production User Feed'
					isRequired
				/>

				<div className='flex items-center gap-3'>
					<Toggle
						checked={formData.enabled}
						onCheckedChange={(checked) => setFormData({ ...formData, enabled: !!checked })}
					/>
					<Text variant='small'>Enabled</Text>
				</div>

				<Text variant='medium' weight='semibold' className='mt-6'>
					SFTP Settings
				</Text>

				<Input
					label='SFTP Username'
					value={formData.sftpUsername}
					onChange={(e) => setFormData({ ...formData, sftpUsername: e.target.value })}
					placeholder='org-username'
					hint='The SFTP username assigned to this organization. This is set when generating SSH keys on the SFTP Keys page.'
					isRequired
				/>

				<Input
					label='S3 Bucket Name'
					value={formData.bucketName}
					onChange={(e) => setFormData({ ...formData, bucketName: e.target.value })}
					placeholder='discover-sftp-prod-123456'
					hint='The S3 bucket backing the SFTP share'
					isRequired
				/>

				<div>
					<Text variant='small' weight='semibold' className='mb-2'>
						Accepted File Extensions
					</Text>
					<div className='flex gap-3'>
						{(['csv', 'xlsx', 'xls'] as const).map((ext) => (
							<label key={ext} className='flex items-center gap-1.5 cursor-pointer'>
								<input
									type='checkbox'
									checked={formData.fileExtensions.includes(ext)}
									onChange={() => toggleExtension(ext)}
									className='rounded border-neutral-300'
								/>
								<Text variant='small'>.{ext}</Text>
							</label>
						))}
					</div>
				</div>

				<DataFeedColumnMappingFields
					description='Map the column headers in your file to the expected user fields.'
					values={formData}
					onChange={(patch) => setFormData({ ...formData, ...patch })}
				/>
			</form>

			{existingConfig && runs && <DataFeedRecentRunsSection runs={runs} />}

			{isLoading && <SpinnerWithBackground />}
		</Modal>
	)
}
